import React from 'react'

import { AMOUNT_OF_ORDERS_TO_FETCH } from '@cowprotocol/common-const'
import { CowSwapSafeAppLink } from '@cowprotocol/ui'

import { plural, t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { TabOrderTypes, OrderTabId } from 'entities/routes/routes.atom'

import { HistoryStatusFilter } from '../../../../utils/getFilteredOrders'
import * as styledEl from '../../Container/OrdersTableContainer.styled'
import { LoadMoreOrdersButton } from '../../LoadMore/Button/LoadMoreOrdersButton'

export interface GetDescriptionOptions {
  currentTab: OrderTabId | null
  hasOrders: boolean
  limit: number
  hasMoreOrders: boolean
  isLoading: boolean
  loadMore: () => void
  orderType: TabOrderTypes
  searchTerm: string
  historyStatusFilter: HistoryStatusFilter
  isSafeViaWc?: boolean
}

export interface GetTitleOptions {
  currentTab: OrderTabId | null
  hasOrders: boolean
  limit: number
  hasMoreOrders: boolean
  searchTerm: string
  historyStatusFilter: HistoryStatusFilter
}

export function getDescription({
  currentTab,
  hasOrders,
  limit,
  hasMoreOrders,
  isLoading,
  loadMore,
  orderType,
  searchTerm,
  historyStatusFilter,
  isSafeViaWc,
}: GetDescriptionOptions): React.ReactNode[] {
  const areOrdersFiltered = hasOrders && (searchTerm || historyStatusFilter !== HistoryStatusFilter.ALL)

  if (!areOrdersFiltered && orderType === TabOrderTypes.ADVANCED && isSafeViaWc) {
    const currentTabText = currentTab === OrderTabId.HISTORY ? t`orders history` : t`your orders`

    return [
      <Trans>
        Use the <CowSwapSafeAppLink /> to see {currentTabText}
      </Trans>,
    ]
  }

  if (currentTab === OrderTabId.OPEN) {
    const learnMoreLink =
      orderType === TabOrderTypes.LIMIT ? (
        <styledEl.ExternalLinkStyled href="https://cow.fi/learn/limit-orders-explained">
          <Trans>Learn more</Trans>
          <styledEl.ExternalArrow />
        </styledEl.ExternalLinkStyled>
      ) : null

    if (hasMoreOrders) {
      return [
        limit === AMOUNT_OF_ORDERS_TO_FETCH
          ? t`Only the ${limit} most recent orders were searched.`
          : t`No open orders found in the ${limit} most recent one.`,
        <>
          <Trans>Press the button below to search older orders, or create a new one!</Trans> {learnMoreLink}
        </>,
        <LoadMoreOrdersButton disabled={isLoading || !hasMoreOrders} onClick={loadMore} />,
      ]
    }

    return [
      t`You don't have any open orders at the moment.`,
      <>
        <Trans>Time to create a new one!</Trans> {learnMoreLink}
      </>,
    ]
  }

  if (!hasOrders) {
    // Generic message if there are no orders because Signing and Unfillable won't appear if there are no orders, and
    // Open is handled above, so this will only show up in History for accounts with 0 orders.
    return [t`You don't have any orders at the moment.`]
  }

  // If there are orders, then we are seeing NoOrdersContent because they have been filtered out by the searchTerm and/or historyStatusFilter:

  if (searchTerm) {
    return [
      historyStatusFilter !== HistoryStatusFilter.ALL
        ? t`Try adjusting your search term or filters`
        : t`Try adjusting your search term`,
    ]
  }

  return [t`Try adjusting your filters`]
}

export function getTitle({
  currentTab,
  hasOrders,
  limit,
  hasMoreOrders,
  searchTerm,
  historyStatusFilter,
}: GetTitleOptions): string {
  if (currentTab === OrderTabId.UNFILLABLE) return t`No unfillable orders`

  if (currentTab === OrderTabId.OPEN) {
    return hasMoreOrders
      ? plural(limit, {
          one: 'No open orders found in your last # order',
          few: 'No open orders found in your last # orders',
          many: 'No open orders found in your last # orders',
          other: 'No open orders found in your last # orders',
        })
      : t`No open orders found`
  }

  if (currentTab === OrderTabId.SIGNING) return t`No signing orders`

  if (!hasOrders || (!searchTerm && historyStatusFilter === HistoryStatusFilter.ALL)) return t`No order history`

  // These only appear in the History tab when using the search or the status filter:

  if (historyStatusFilter === HistoryStatusFilter.FILLED) return t`No filled orders found`
  if (historyStatusFilter === HistoryStatusFilter.CANCELLED) return t`No cancelled orders found`
  if (historyStatusFilter === HistoryStatusFilter.EXPIRED) return t`No expired orders found`

  return t`No matching orders found`
}
